import { useMutation, useQueryClient } from "@tanstack/react-query";
import { phoneVerificationStatusKeys } from "Api/Queries/Auth/usePhoneVerificationStatusQuery";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { USERS_LIST_QUERY_KEY } from "Api/Queries/User/useUsersListQuery";
import { type PartyDto, putPartiesPublicID } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Parameters = { publicID: string; data: PartyDto };

type Props = {
  onSuccess?: (parameters: Parameters) => void;
  onError?: (error: unknown) => void;
};

export const usePartyMutation = ({ onSuccess, onError }: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ publicID, data }: Parameters) => putPartiesPublicID(data, publicID),
    ),
    onSuccess: (_, parameters: Parameters) => {
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(parameters.publicID),
      });
      queryClient.invalidateQueries({
        queryKey: [USERS_LIST_QUERY_KEY],
      });
      // Editing the party phone un-verifies it server-side (IsVerified = Phone == VerifiedPhone),
      // so the person-level verification status must be refetched along with the party.
      queryClient.invalidateQueries({
        queryKey: phoneVerificationStatusKeys.all,
      });
      onSuccess?.(parameters);
    },
    onError,
  });
};
